import * as Sentry from "@sentry/react"
import { onAuthStateChanged } from "firebase/auth"
import { startTransition, useEffect, useState } from "react"
import { clearPersistedState, useAppDispatch, useAppSelector } from "../redux"
import { AppActions } from "../redux/appSlice"
import {
  createCreator,
  getCreator,
  getFeedbacks,
  isUserAdmin,
} from "../services"
import { getCreatorFromUser } from "../types"
import {
  firebaseAuth,
  getEntitiesWithFeedback,
  resetMixpanel,
  setUserOnMixpanel,
} from "../utils"
import { getCreatorCustomBranding } from "../utils/custom-branding"

export function useFirebaseAuth() {
  const [loading, setLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()
  const feedbackEntitiesLoaded = useAppSelector(
    (state) => state.app.feedbackEntitiesLoaded,
  )

  useEffect(() => {
    let mounted = true

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      if (!mounted) return

      if (user) {
        if (!user.emailVerified) return

        setUserOnMixpanel(user)

        let creator
        try {
          creator = await getCreator(user.uid)
        } catch (error) {
          console.error("Error fetching creator:", error)

          if (error instanceof Error && error.message.includes("not found")) {
            try {
              creator = await createCreator(
                getCreatorFromUser({
                  email: user.email,
                  name: user.displayName || "",
                  uid: user.uid,
                  termsAccepted: false,
                }),
              )
            } catch (createError) {
              console.error("Error creating creator:", createError)
              if (mounted) {
                setLoading(false)
              }
              return
            }
          } else {
            if (mounted) {
              setLoading(false)
            }
            return
          }
        }

        if (mounted && creator) {
          startTransition(() => {
            try {
              dispatch(
                AppActions.updateUser({
                  uid: user.uid,
                  name: user.displayName || "",
                  email: user.email,
                  photoURL: user.photoURL,
                  subscriptionPlan: creator.subscriptionPlan,
                  subscriptionState: creator.subscriptionState,
                  subscriptionBillingCycleAnchor:
                    creator.subscriptionBillingCycleAnchor,
                  termsAccepted: creator.termsAccepted ? true : false,
                  ...getCreatorCustomBranding(creator),
                }),
              )
            } catch (error) {
              console.error("Failed to update user state:", error)
              Sentry.captureException(error, {
                tags: {
                  hook: "useFirebaseAuth",
                  operation: "update_user_state",
                },
                user: { uid: user.uid, email: user.email },
              })
            }
          })
        }

        const isAdmin = await isUserAdmin()

        if (!feedbackEntitiesLoaded) {
          try {
            const feedbacks = await getFeedbacks({ cursor: "", limit: 1000 })
            const entities = getEntitiesWithFeedback(feedbacks)

            if (mounted) {
              dispatch(AppActions.updateEntitiesWithFeedback(entities))
            }
          } catch (error) {
            console.error("Failed to load feedback entities:", error)
            Sentry.captureException(error, {
              tags: {
                hook: "useFirebaseAuth",
                operation: "load_feedback_entities",
              },
              user: { uid: user.uid, email: user.email },
            })
          }
        }

        if (mounted) {
          startTransition(() => {
            try {
              dispatch(AppActions.updateIsUserAdmin(isAdmin))
              setLoading(false)
            } catch (error) {
              console.error("Failed to update admin/entities state:", error)
              Sentry.captureException(error, {
                tags: {
                  hook: "useFirebaseAuth",
                  operation: "update_admin_entities_state",
                },
                user: { uid: user.uid, email: user.email },
              })
              setLoading(false)
            }
          })
        }
      } else {
        if (mounted) {
          const resetStateAsync = async () => {
            try {
              await clearPersistedState()
              startTransition(() => {
                dispatch(AppActions.resetState())
                resetMixpanel()
                setLoading(false)
              })
            } catch (error) {
              console.error("Failed to reset state:", error)
              Sentry.captureException(error, {
                tags: { hook: "useFirebaseAuth", operation: "reset_state" },
              })
              setLoading(false)
            }
          }
          resetStateAsync()
        }
      }
    })

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [dispatch])

  return [loading]
}
